/**
 * Оболочка личного кабинета: боковая навигация + рабочая область.
 *
 * Композиция из промпта 03: слева вертикальная навигация, справа контент.
 * На мобильном навигация сворачивается в выдвижную панель — настройки
 * маршрута правят с телефона прямо перед выходом на улицу.
 */

import { useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import {
  Gauge,
  History,
  LogOut,
  Menu,
  MonitorPlay,
  Route as RouteIcon,
  Smartphone,
  UserCog,
  X,
} from 'lucide-react';
import { useAuth } from '../lib/auth-context';
import { Logo } from './Logo';

interface NavItem {
  to: string;
  label: string;
  icon: typeof Gauge;
  hint: string;
}

const NAV_ITEMS: readonly NavItem[] = [
  { to: '/dashboard', label: 'Мой GPS-виджет', icon: Gauge, hint: 'Телеметрия и карта' },
  { to: '/settings/route', label: 'Маршрут', icon: RouteIcon, hint: 'Старт, цель, параметры' },
  { to: '/settings/overlay', label: 'OBS-ссылка', icon: MonitorPlay, hint: 'Внешний вид и доступ' },
  { to: '/settings/devices', label: 'GPS-источники', icon: Smartphone, hint: 'Телефон и трекеры' },
  { to: '/history', label: 'История', icon: History, hint: 'Пройденные маршруты' },
  { to: '/account', label: 'Аккаунт', icon: UserCog, hint: 'Профиль и удаление' },
];

export function AppShell() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  // Переход между страницами закрывает мобильное меню.
  useEffect(() => setMenuOpen(false), [location.pathname]);

  return (
    <div className="flex min-h-screen flex-col lg:flex-row">
      {/* Мобильная шапка */}
      <header className="flex items-center justify-between border-b border-edge bg-surface/95 px-4 py-3 backdrop-blur lg:hidden">
        <Link to="/dashboard" aria-label="GPS Tracker — на главную">
          <Logo compact />
        </Link>

        <button
          type="button"
          className="btn-ghost px-2.5 py-2"
          onClick={() => setMenuOpen((open) => !open)}
          aria-expanded={menuOpen}
          aria-controls="app-navigation"
          aria-label={menuOpen ? 'Закрыть меню' : 'Открыть меню'}
        >
          {menuOpen ? <X className="h-5 w-5" aria-hidden /> : <Menu className="h-5 w-5" aria-hidden />}
        </button>
      </header>

      <nav
        id="app-navigation"
        className={`${
          menuOpen ? 'flex' : 'hidden'
        } w-full shrink-0 flex-col gap-1 border-b border-edge bg-surface p-4 lg:flex lg:w-72 lg:border-b-0 lg:border-r lg:p-5`}
        aria-label="Разделы личного кабинета"
      >
        <div className="mb-6 hidden lg:block">
          <Link to="/dashboard" aria-label="GPS Tracker — на главную">
            <Logo />
          </Link>
        </div>

        {NAV_ITEMS.map(({ to, label, icon: Icon, hint }) => (
          <NavLink
            key={to}
            to={to}
            className={({ isActive }) =>
              `group flex items-start gap-3 rounded-card border px-3 py-2.5 transition-colors ${
                isActive
                  ? 'border-lime/35 bg-lime-dim text-ink'
                  : 'border-transparent text-muted hover:border-edge hover:bg-elevated hover:text-ink'
              }`
            }
          >
            {({ isActive }) => (
              <>
                <Icon
                  className={`mt-0.5 h-4 w-4 shrink-0 ${isActive ? 'text-lime' : 'text-muted group-hover:text-ink'}`}
                  aria-hidden
                />
                <span className="flex min-w-0 flex-col">
                  <span className="text-sm font-semibold leading-tight">{label}</span>
                  <span className="mt-0.5 text-xs text-muted">{hint}</span>
                </span>
              </>
            )}
          </NavLink>
        ))}

        <div className="mt-auto flex flex-col gap-3 pt-6">
          {user ? (
            <div className="flex items-center gap-3 rounded-card border border-edge bg-elevated px-3 py-2.5">
              {user.avatarUrl ? (
                <img
                  src={user.avatarUrl}
                  alt=""
                  className="h-9 w-9 rounded-full border border-violet/40"
                  width={36}
                  height={36}
                  loading="lazy"
                />
              ) : (
                <span
                  className="flex h-9 w-9 items-center justify-center rounded-full border border-violet/40 bg-violet-dim text-sm font-bold text-violet"
                  aria-hidden
                >
                  {user.displayName.slice(0, 1).toUpperCase()}
                </span>
              )}

              <span className="flex min-w-0 flex-col">
                <span className="truncate text-sm font-semibold text-ink">
                  {user.displayName}
                </span>
                <span className="truncate text-xs text-muted">@{user.twitchLogin}</span>
              </span>
            </div>
          ) : null}

          <button type="button" className="btn-ghost w-full" onClick={() => void logout()}>
            <LogOut className="h-4 w-4" aria-hidden />
            Выйти
          </button>
        </div>
      </nav>

      <main className="min-w-0 flex-1 p-4 lg:p-7">
        <Outlet />
      </main>
    </div>
  );
}
